// ./src/utils/eventSelling.ts
//
// The event's SELLING choices — who pays the fees, and the currency list — for every screen that edits them: Details &
// Settings (Payments & selling) and the create wizard's Tickets step (ROADMAP A2b #62 P6b).
//
// ⚠ ONE LIST, because two screens labelling the same choice differently is a host picking "I cover all fees" in one
// place and reading "Guests pay our service fee" in the other.
// ⚠ `feeMode` is the choice; `serviceFeePayer` is the older column derived from it. Both are written together — the
// server reads either, and an update that sends only one leaves the other describing a different deal.
//
// ⚠ NODE-SAFE ON PURPOSE: `check-event-setup` RUNS these. `EventService` pulls in the API client and the brand config
// (`import.meta.env`), which do not exist under Node — so its constants are mirrored here as literals TYPED against its
// unions. A literal that stops being a valid `FeeMode` / `ServiceFeePayer` is a tsc error, not a silent drift.
import type { TFunction } from 'i18next';

import type { FeeMode, ServiceFeePayer } from './api/EventService';
import { CURRENCY_OPTIONS, getCurrencyShortLabel } from '../config/currencyOptions';

const FEE_MODE = {
  ABSORB: 'absorb',
  SERVICE: 'service',
  SERVICE_PROCESSOR: 'service_processor',
} as const satisfies Record<string, FeeMode>;

const SERVICE_FEE_PAYER = {
  OWNER: 'owner',
  GUEST: 'guest',
} as const satisfies Record<string, ServiceFeePayer>;

export interface SellingOption {
  value: string;
  label: string;
}

/** "Who pays the fees" — the three modes, in the order a host reads them. */
export const feeModeOptions = (t: TFunction): SellingOption[] => [
  { value: FEE_MODE.ABSORB, label: t('events:selling.feeModeAbsorb', { defaultValue: 'I cover all fees' }) },
  { value: FEE_MODE.SERVICE, label: t('events:selling.feeModeService', { defaultValue: 'Guests pay our service fee' }) },
  {
    value: FEE_MODE.SERVICE_PROCESSOR,
    label: t('events:selling.feeModeServiceProcessor', { defaultValue: 'Guests pay service + card fees' }),
  },
];

/** The stored mode, or the one the older `serviceFeePayer` column implies when `feeMode` is not set. */
export const feeModeOf = (event: { feeMode?: string | null; serviceFeePayer?: string | null }): string =>
  event.feeMode ?? (event.serviceFeePayer === SERVICE_FEE_PAYER.GUEST ? FEE_MODE.SERVICE : FEE_MODE.ABSORB);

/** The `serviceFeePayer` a mode means — sent WITH every `feeMode` write. */
export const serviceFeePayerFor = (feeMode: string): ServiceFeePayer =>
  feeMode === FEE_MODE.ABSORB ? SERVICE_FEE_PAYER.OWNER : SERVICE_FEE_PAYER.GUEST;

/** Every currency an event can be priced in, as select options. */
export const currencySelectOptions = (): SellingOption[] =>
  CURRENCY_OPTIONS.map((c) => ({ value: c.value, label: getCurrencyShortLabel(c) }));
